import React from "react";
import { Box, Breadcrumbs, Button, CssBaseline, Grid, Link, Stack, ThemeProvider, Typography, useTheme, useMediaQuery } from "@mui/material";
import { lightTheme } from "./Themes";
import { TopBar } from "./components/TopBar";
import { NavigationMenu } from "./components/NavigationMenu";
import { Portada } from "./components/Portada";
import { Skyline } from "./components/Skyline";
import { BottomBanner } from "./components/BottomBanner";

import logo_ursi from './images/logo_ursi.png'
import { globalMaxWidth } from "./GlobalConfigs";

import nivaria from './images/alojamientos/nivaria.jpg'
import aguere from './images/alojamientos/aguere.jpg'
import gran_hotel from './images/alojamientos/gran_hotel.jpg'
import nh_tenerife from './images/alojamientos/nh_tenerife.jpg'
import silken_atlantida from './images/alojamientos/silken_atlantida.jpg'

import PlaceIcon from '@mui/icons-material/Place';
import EuroIcon from '@mui/icons-material/Euro';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import StarIcon from '@mui/icons-material/Star';



function BreadcrumbSection() {
    return (
        <Breadcrumbs aria-label="breadcrumb" margin={2}>
            <Link underline="hover" color="inherit" href="/">
                Home
            </Link>
            <Typography sx={{ color: 'text.primary' }}>Alojamiento</Typography>
        </Breadcrumbs>
    );
}


function Menu({ idx, setIdx }) {
    let elements = [
        'La Laguna',
        'Santa Cruz'
    ]
    return (
        <Grid container spacing={2} margin={2} justifyContent='center' >
            {elements.map((element, index) => (
                <Grid item>
                    <Button onClick={() => {
                        setIdx(index);
                    }}
                        data-active={idx === index ? '' : undefined}
                        sx={{
                            height: '100%',
                            fontSize: { xs: '0.9rem', sm: '1.1rem' },
                            px: { xs: 3, sm: 5 },
                            py: 1.5,
                            '&[data-active]': {
                                backgroundColor: 'action.selected',
                                fontWeight: 'bold',
                                '&:hover': {
                                    backgroundColor: 'action.selectedHover',
                                },
                            },
                        }}
                    >
                        {element}
                    </Button>
                </Grid>
            ))}
        </Grid>
    );
}



function AlojamientoCard({ alojamiento }) {
    let { picture, name, description, weblink, mapslink } = alojamiento;
    return (
        <Box
            margin={2}
            sx={{
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                borderRadius: 4,
                maxWidth: globalMaxWidth,
                width: '90%',
                bgcolor: 'background.paper',
                overflow: 'hidden',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 12px 30px rgba(0,0,0,0.12)',
                }
            }}
        >
            <Grid container spacing={0}>
                <Grid item xs={12} md={4} lg={3}>
                    <Box
                        component='img'
                        src={picture}
                        alt={name}
                        loading="lazy"
                        sx={{
                            width: '100%',
                            height: '100%',
                            minHeight: { xs: 200, md: 220 },
                            maxHeight: { xs: 300, md: 350 },
                            objectFit: 'cover',
                            display: 'block'
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={8} lg={9}>
                    <Stack spacing={2} alignItems='flex-start' sx={{ p: { xs: 3, md: 5 } }}>
                        <Typography variant='h4' color='primary' align='left' sx={{ fontWeight: 800, letterSpacing: -0.5 }}>
                            {name}
                        </Typography>
                        <Box sx={{ width: 1 }}>
                            {description}
                        </Box>
                        <Stack direction='row' spacing={2} justifyContent='flex-start' sx={{ width: 1, mt: 1 }}>
                            <Button
                                href={weblink}
                                target="_blank"
                                variant='contained'
                                sx={{
                                    flex: 1,
                                    borderRadius: 2,
                                    py: 1,
                                    textTransform: 'none',
                                    fontWeight: 'bold',
                                    boxShadow: 'none',
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                    '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }
                                }}
                            >
                                Visitar Sitio Web
                            </Button>
                            <Button
                                href={mapslink}
                                target="_blank"
                                variant='outlined'
                                startIcon={<PlaceIcon />}
                                sx={{
                                    flex: 1,
                                    borderRadius: 2,
                                    py: 1,
                                    textTransform: 'none',
                                    fontWeight: 'bold',
                                    borderWidth: 2,
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                    '&:hover': { borderWidth: 2 }
                                }}
                            >
                                Google Maps
                            </Button>
                        </Stack>
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    )
}

function AlojamientoPage() {
    let [idx, setIdx] = React.useState(0); // Default to La Laguna

    let hotelesSantaCruz = [
        {
            picture: nh_tenerife,
            name: 'NH Tenerife',
            description: <Stack spacing={1.5} alignItems='flex-start'>
                <Stack direction='row' spacing={0.5}>
                    {[1, 2, 3, 4].map((i) => <StarIcon key={i} sx={{ color: '#ffc107', fontSize: 20 }} />)}
                </Stack>
                <Typography variant='body1' align='justify' sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                    Moderno hotel de 4 estrellas en pleno centro urbano, muy próximo a zonas comerciales y culturales.
                </Typography>
                <Stack spacing={1}>
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <EuroIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        <Typography variant='body2'>
                            <strong>Precio medio:</strong> 65–75 € (doble), desayuno incluido.
                        </Typography>
                    </Stack>
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <DirectionsWalkIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        <Typography variant='body2'>
                            A 3 min. de la parada del tranvía hacia La Laguna.
                        </Typography>
                    </Stack>
                </Stack>
            </Stack>,
            weblink: 'https://www.nh-hoteles.es/hotel/nh-tenerife',
            mapslink: 'https://www.google.com/maps/search/?api=1&query=NH+Tenerife+Santa+Cruz+de+Tenerife'
        },
        {
            picture: silken_atlantida,
            name: 'Hotel Silken Atlántida',
            description: <Stack spacing={1.5} alignItems='flex-start'>
                <Stack direction='row' spacing={0.5}>
                    {[1, 2, 3, 4].map((i) => <StarIcon key={i} sx={{ color: '#ffc107', fontSize: 20 }} />)}
                </Stack>
                <Typography variant='body1' align='justify' sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                    Hotel de 4 estrellas ubicado en el centro comercial y financiero de Santa Cruz, ideal para quienes buscan comodidad urbana.
                </Typography>
                <Stack spacing={1}>
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <EuroIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        <Typography variant='body2'>
                            <strong>Precio medio:</strong> 65–70 € (doble), desayuno incluido.
                        </Typography>
                    </Stack>
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <DirectionsWalkIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        <Typography variant='body2'>
                            A 5 min. a pie del Intercambiador (tranvía y autobús).
                        </Typography>
                    </Stack>
                </Stack>
            </Stack>,
            weblink: 'https://www.hoteles-silken.com/es/hotel-silken-atlantida-santa-cruz-tenerife/',
            mapslink: 'https://www.google.com/maps/search/?api=1&query=Hotel+Silken+Atlantida+Santa+Cruz+de+Tenerife'
        },
    ]

    let hotelesLaLaguna = [
        {
            picture: nivaria,
            name: 'Hotel Nivaria',
            description: <Stack spacing={1.5} alignItems='flex-start'>
                <Stack direction='row' spacing={0.5}>
                    {[1, 2, 3, 4].map((i) => <StarIcon key={i} sx={{ color: '#ffc107', fontSize: 20 }} />)}
                </Stack>
                <Typography variant='body1' align='justify' sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                    Hotel con encanto de 4 estrellas ubicado en una casa señorial del siglo XVI, en pleno centro histórico.
                </Typography>
                <Stack spacing={1}>
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <EuroIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        <Typography variant='body2'>
                            <strong>Precio medio:</strong> 95€ (ind.), 105€ (doble), 130€ (triple), desayuno incluido.
                        </Typography>
                    </Stack>
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <DirectionsWalkIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        <Typography variant='body2'>
                            A 5 min. del tranvía y 20 min. caminando de la Escuela de Ingeniería.
                        </Typography>
                    </Stack>
                </Stack>
            </Stack>,
            weblink: 'https://www.lagunanivaria.com/',
            mapslink: 'https://www.google.com/maps/search/?api=1&query=Hotel+Laguna+Nivaria+San+Cristobal+de+la+Laguna'
        },
        {
            picture: aguere,
            name: 'Hotel Aguere',
            description: <Stack spacing={1.5} alignItems='flex-start'>
                <Stack direction='row' spacing={0.5}>
                    {[1, 2, 3, 4].map((i) => <StarIcon key={i} sx={{ color: '#ffc107', fontSize: 20 }} />)}
                </Stack>
                <Typography variant='body1' align='justify' sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                    Hotel de 4 estrellas situado en el emblemático edificio de los Marqueses de Torre Hermosa, declarado Patrimonio Histórico-Artístico.
                </Typography>
                <Stack spacing={1}>
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <EuroIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        <Typography variant='body2'>
                            <strong>Precio medio:</strong> 62 € (individual), 80 € (doble), desayuno incluido.
                        </Typography>
                    </Stack>
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <DirectionsWalkIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        <Typography variant='body2'>
                            A 5 min. del tranvía y 15 min. a pie de la Escuela de Ingeniería.
                        </Typography>
                    </Stack>
                </Stack>
            </Stack>,
            weblink: 'https://hotelaguere.es/',
            mapslink: 'https://www.google.com/maps/search/?api=1&query=Hotel+Aguere+San+Cristobal+de+la+Laguna'
        },
        {
            picture: gran_hotel,
            name: 'La Laguna Gran Hotel',
            description: <Stack spacing={1.5} alignItems='flex-start'>
                <Stack direction='row' spacing={0.5}>
                    {[1, 2, 3, 4].map((i) => <StarIcon key={i} sx={{ color: '#ffc107', fontSize: 20 }} />)}
                </Stack>
                <Typography variant='body1' align='justify' sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                    Es un hotel 4 estrella ideal tanto para viajes de negocios como de vacaciones. Destaca su terraza de la cafetería y la azotea.
                </Typography>
                <Stack spacing={1}>
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <EuroIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        <Typography variant='body2'>
                            <strong>Precio medio:</strong> Entre 116 € y 135 € para habitaciones estándar.
                        </Typography>
                    </Stack>
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <DirectionsWalkIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        <Typography variant='body2'>
                            A 15 minutos del tranvía.
                        </Typography>
                    </Stack>
                </Stack>
            </Stack>,
            weblink: 'https://lalagunagranhotel.com/',
            mapslink: 'https://www.google.com/maps/search/?api=1&query=La+Laguna+Gran+Hotel+San+Cristobal+de+la+Laguna'
        },
    ]
    return (
        <ThemeProvider theme={lightTheme}>
            <div className="App">
                <CssBaseline />

                <Stack alignItems='center'>
                    <TopBar />
                    <NavigationMenu />
                    <Portada with_buttons={false} construction_text={false} />
                    <BreadcrumbSection />
                    <Stack direction={{ xs: 'column', md: 'row' }} alignItems='center' spacing={4} margin={4} sx={{ width: '80%' }}>
                        <Box component='img' sx={{ height: { xs: 80, sm: 100, md: 110, lg: 120, xl: 160, x2l: 200 } }}
                            src={logo_ursi}
                            alt={"Logo URSI"}
                            loading="lazy" />
                        <Stack spacing={2}>
                            <Typography variant='body1' align='justify'>
                                Te invitamos a descubrir los exclusivos alojamientos para
                                profesionales y congresistas durante el XLI Simposio Nacional de la Unión Científica
                                Internacional de Radio (URSI 2026), que tendrá lugar en La Laguna del 2 al 4 de Septiembre
                                de 2026.
                            </Typography>

                            <Typography variant='body1' align='justify'>
                                Con una ubicación privilegiada y una oferta de alojamiento de calidad, San Cristóbal de La Laguna es la opción
                                ideal para tu estancia durante este importante acontecimiento del sector TIC.
                            </Typography>
                        </Stack>
                    </Stack>
                    <Menu idx={idx} setIdx={setIdx} />
                    {idx === 0 ?
                        hotelesLaLaguna.map((hotel) => (
                            <AlojamientoCard alojamiento={hotel} key={hotel.name} />
                        ))
                        :
                        hotelesSantaCruz.map((hotel) => (
                            <AlojamientoCard alojamiento={hotel} key={hotel.name} />
                        ))
                    }
                    <Skyline />
                    <BottomBanner />
                </Stack>
            </div>
        </ThemeProvider>
    )
}

export default AlojamientoPage;
